import { useNavigate } from 'react-router-dom'

import { Box, Callout, Text, useConfirmationDialog } from '@factorialco/deprecated-design-system'
import { PerformanceProcessInitiateAgreementDocument } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface UseInitiateAgreementParams {
  republishEvaluationsEnabled: boolean
}
export const useInitiateAgreement = ({
  republishEvaluationsEnabled,
}: UseInitiateAgreementParams) => {
  const { openDialog } = useConfirmationDialog()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const [initiateAgreementMutation] = useMutation(PerformanceProcessInitiateAgreementDocument, {
    extractorFn: (data) => data?.performanceMutations?.initiatePerformanceAgreement,
  })

  const initiateAgreement = ({
    processId,
    targetId,
    targetName,
  }: {
    processId?: number
    targetId?: string
    targetName: string
  }) => {
    const contentLabel = i18n.t('performance_process.agreements.initiate_dialog.content', {
      name: targetName,
    })
    const editingDisabledLabel = republishEvaluationsEnabled
      ? i18n.t('performance_process.agreements.initiate_dialog.editing_disabled')
      : null

    openDialog({
      title: i18n.t('performance_process.agreements.initiate_dialog.title', {
        name: targetName,
      }),
      description: (
        <Box gap="s16">
          <Text.Normal>{contentLabel}</Text.Normal>
          {editingDisabledLabel && <Callout type="warning" description={editingDisabledLabel} />}
        </Box>
      ),
      confirmLabel: i18n.t('performance_process.agreements.initiate_dialog.continue'),
      illustration: 'document-handshake',
      onConfirm: async () => {
        if (!processId || !targetId) return

        const result = await initiateAgreementMutation({ variables: { targetId, processId } })
        const handled = handleResult(result, (formErrors) => {
          const messages = formErrors.messages
          if (messages) {
            app.setFlash({
              type: 'wrong',
              message: messages ?? i18n.t('common.error'),
            })
          }
        })

        if (handled.success) {
          const agreementId = handled.data?.agreement?.id
          if (agreementId)
            navigate(
              navigation.performanceProcess.reviews.review.target.agreement.fullPath({
                targetId: targetId ?? '',
                reviewId: processId ?? '',
                agreementId,
              })
            )
        }
      },
    })
  }

  return { initiateAgreement }
}
